import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { map, Observable, switchMap, of } from 'rxjs';
import { IAuthProvider } from '../providers/auth-provider.interface';
import { User } from '../models/user.model';
import { isPlatformBrowser } from '@angular/common';

/**
 * Logto Authentication Provider Implementation
 * Uses angular-auth-oidc-client for OIDC flow with Logto
 */
@Injectable()
export class LogtoAuthService implements IAuthProvider {
    public isAuthenticated$: Observable<boolean>;
    public userData$: Observable<User | null>;
    public accessToken$: Observable<string | null>;
    private platformId = inject(PLATFORM_ID);

    private oidcSecurityService = inject(OidcSecurityService);
    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            // Map OIDC auth result to boolean observable
            this.isAuthenticated$ = this.oidcSecurityService.isAuthenticated$.pipe(
                map(result => result.isAuthenticated)
            );

            this.userData$ = this.oidcSecurityService.userData$.pipe(
                map(userData => userData.userData ? this.mapToUser(userData.userData) : null)
            );

            // Get access token directly - getAccessToken() already returns Observable<string>
            this.accessToken$ = this.oidcSecurityService.getAccessToken();
        } else {
            // Server-side default values
            this.isAuthenticated$ = of(false);
            this.userData$ = of(null);
            this.accessToken$ = of(null);
        }
    }

    initialize(): void {
        // Any initialization logic if needed
        // OIDC client is automatically initialized via provideAuth in app.config
    }

    signIn(): void {
        if (isPlatformBrowser(this.platformId)) {
            this.oidcSecurityService.authorize();
        }
    }

    signOut(): Observable<unknown> {
        if (isPlatformBrowser(this.platformId)) {
            return this.oidcSecurityService.logoff();
        }
        return of(null);
    }

    /**
     * Map OIDC user data to our User model
     */
    private mapToUser(userData: any): User {
        return {
            id: userData.sub || userData.id || '',
            email: userData.email,
            name: userData.name || userData.username,
            picture: userData.picture,
            emailVerified: userData.email_verified,
            metadata: userData
        };
    }
}
